// What? A React component for displaying test results for a document type section.
// Why? To show users the generated prompts, inputs, and source documents for testing.
// How? Accordion-based expandable section showing inputs, source documents, and generated prompts.

import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from "@mui/joy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { TestPromptSection } from "../../../types/documentTypes";
import SourceDocument from "./SourceDocument";

interface TestSectionProps {
  section: TestPromptSection;
}

const TestSection: React.FC<TestSectionProps> = ({ section }) => {
  return (
    <Accordion
      sx={{
        mb: 2,
        "&:hover": {
          boxShadow: "sm",
        },
      }}
    >
      <AccordionSummary indicator={<ExpandMoreIcon />}>
        <Typography level="title-lg" sx={{ fontWeight: 500 }}>
          {section.title}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Stack spacing={3} sx={{ pt: 2 }}>
          {section.inputs.length > 0 && (
            <Box>
              <Typography
                level="title-sm"
                sx={{
                  mb: 1.5,
                  color: "text.secondary",
                }}
              >
                Data Inputs
              </Typography>
              <Stack spacing={1}>
                {section.inputs.map((input, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      p: 1.5,
                      bgcolor: "background.level1",
                      borderRadius: "sm",
                    }}
                  >
                    <Typography
                      level="body-sm"
                      sx={{
                        fontWeight: 500,
                        mr: 1,
                        minWidth: "fit-content",
                        color: "text.primary",
                      }}
                    >
                      {input.name}:
                    </Typography>
                    <Typography
                      level="body-sm"
                      sx={{
                        fontFamily: "code",
                        color: "text.secondary",
                      }}
                    >
                      {input.value}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {section.sourceDocuments.length > 0 && (
            <Box>
              <Typography
                level="title-sm"
                sx={{
                  mb: 1.5,
                  color: "text.secondary",
                }}
              >
                Source Documents
              </Typography>
              <Stack spacing={1}>
                {section.sourceDocuments.map((doc, index) => (
                  <SourceDocument
                    key={index}
                    fileName={doc.fileName}
                    content={doc.content}
                  />
                ))}
              </Stack>
            </Box>
          )}

          <Box>
            <Typography
              level="title-sm"
              sx={{
                mb: 1.5,
                color: "text.secondary",
              }}
            >
              Generated Prompt
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                bgcolor: "background.level1",
                borderRadius: "sm",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "code",
                fontSize: "sm",
                lineHeight: 1.5,
                overflow: "auto",
                maxHeight: "400px",
              }}
            >
              {section.prompt}
            </Box>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default TestSection;
